import { usePhoneLayout } from "../lib/PhoneLayout";
import { useEditorialText } from "../lib/EditorialText";
import { Fragment, useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion, useScroll, useTransform } from "framer-motion";

function readReducedData() {
  return typeof window !== "undefined" && (
    window.navigator.connection?.saveData === true ||
    window.matchMedia?.("(prefers-reduced-data: reduce)").matches === true
  );
}

export function useReducedData() {
  const [reduced, setReduced] = useState(readReducedData);
  useEffect(() => {
    const connection = window.navigator.connection;
    const query = window.matchMedia?.("(prefers-reduced-data: reduce)");
    const update = () => setReduced(readReducedData());
    connection?.addEventListener?.("change", update);
    if (query?.addEventListener) query.addEventListener("change", update);
    else query?.addListener?.(update);
    update();
    return () => {
      connection?.removeEventListener?.("change", update);
      if (query?.removeEventListener) query.removeEventListener("change", update);
      else query?.removeListener?.(update);
    };
  }, []);
  return reduced;
}

function Word({ children, progress, range, from, to }) {
  const color = useTransform(progress, range, [from, to]);
  return (
    <motion.span style={{ color }} className={children.startsWith("S H & SONS ®") ? "brand-name" : "inline-block"}>
      {children}
    </motion.span>
  );
}

function StaticWords({ words, color }) {
  return words.map((word, i) => (
    <Fragment key={`${word}-${i}`}>
      <span style={{ color }} className={word.startsWith("S H & SONS ®") ? "brand-name" : "inline-block"}>{word}</span>
      {i < words.length - 1 ? " " : null}
    </Fragment>
  ));
}

function AnimatedWords({ target, words, from, to }) {
  const { scrollYProgress } = useScroll({ target, offset: ["start 92%", "start 52%"] });
  return words.map((word, i) => (
    <Fragment key={`${word}-${i}`}>
      <Word progress={scrollYProgress} range={[i / words.length, (i + 1) / words.length]} from={from} to={to}>{word}</Word>
      {i < words.length - 1 ? " " : null}
    </Fragment>
  ));
}

function VisibleWords({ target, words, from, to }) {
  const nearViewport = useInView(target, { margin: "160px 0px" });
  if (nearViewport) return <AnimatedWords target={target} words={words} from={from} to={to} />;
  const aboveViewport = target.current && target.current.getBoundingClientRect().bottom < 0;
  return <StaticWords words={words} color={aboveViewport ? to : from} />;
}

export default function WordReveal({ text, as: Tag = "h2", className = "", dark = false, ...rest }) {
  text = useEditorialText(text);
  const phone = usePhoneLayout();
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const reducedData = useReducedData();
  const words = String(text ?? "").match(/S H & SONS ®[.,;:!?]?|\S+/g) || [];
  const hasBrand = words.some((word) => word.startsWith("S H & SONS ®"));
  const from = dark ? "#4f4f4f" : "#bdbdbd";
  const to = dark ? "#ffffff" : "#1b1b1b";
  return (
    <Tag ref={ref} className={className + (hasBrand ? " brand-aware-text" : "")} {...rest}>
      {phone || reduced || reducedData
        ? <StaticWords words={words} color={to} />
        : <VisibleWords target={ref} words={words} from={from} to={to} />}
    </Tag>
  );
}
